import React, { useEffect, useRef } from "react";
import { useLocation, useOutlet } from "react-router-dom";

const MAX_CACHED_ROUTES = 12;

/**
 * Keeps recently visited routes mounted (hidden) so local state survives back navigation.
 */
const KeepAliveOutlet = ({ scrollContainerRef }) => {
  const location = useLocation();
  const outlet = useOutlet();
  const cacheRef = useRef(new Map());
  const orderRef = useRef([]);
  const scrollPositionsRef = useRef({});

  const cacheKey = location.pathname;

  if (outlet) {
    if (!cacheRef.current.has(cacheKey)) {
      orderRef.current.push(cacheKey);
      while (orderRef.current.length > MAX_CACHED_ROUTES) {
        const evict = orderRef.current.shift();
        if (evict && evict !== cacheKey) {
          cacheRef.current.delete(evict);
          delete scrollPositionsRef.current[evict];
        }
      }
    } else {
      orderRef.current = orderRef.current.filter((k) => k !== cacheKey);
      orderRef.current.push(cacheKey);
    }
    cacheRef.current.set(cacheKey, outlet);
  }

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return undefined;

    el.scrollTop = scrollPositionsRef.current[cacheKey] || 0;

    return () => {
      scrollPositionsRef.current[cacheKey] = el.scrollTop;
    };
  }, [cacheKey, scrollContainerRef]);

  return (
    <>
      {orderRef.current.map((path) => {
        const cached = cacheRef.current.get(path);
        if (!cached) return null;
        const active = path === cacheKey;
        return (
          <div
            key={path}
            className={active ? "min-h-0" : "hidden"}
            aria-hidden={!active}
          >
            {cached}
          </div>
        );
      })}
    </>
  );
};

export default KeepAliveOutlet;
